import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { emberApi, isAuthRequired, type OwnerEmberDetailResponse, type OwnerRead, type ReadNote } from '../api';
import { toast } from '../../../design-system/components';
import { trackFailed } from '../../../../../shared-web/track';
import { timeAgo } from '../format';
import { navigate } from '../router';
import { useRequireAuth } from '../use-require-auth';
import { useSession } from '../../auth';
import { PageBack } from '../components/PageBack';
import { Link } from '../components/Link';
import { PageHome } from '../components/PageHome';
import { HandoffButton } from '../components/HandoffButton';
import { PrivateImpactList } from '../components/PrivateImpactList';
import { EmberBody } from '../components/EmberBody';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { notifyKnocksChanged } from '../components/use-knocks';
import { LIVE_EMBER_EVENT, type LiveEvent } from '../live';
import { ControlRow } from '../components/ControlRow';
import { Switch } from '../components/Switch';
import { NoteComposer, NoteThread } from '../components/ReadNotes';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { assignedAvatarComboKey } from '../../../../../shared/ember-avatars.js';

/** The most recent readers drawn as a stack; the count carries the rest. */
const READER_STACK_MAX = 8;
import { BottomSheet } from '../components/BottomSheet';
import { emberAbout, talkAboutHref } from '../talk-about';
import './OwnerEmberDetail.css';

export interface OwnerEmberDetailPageProps {
  emberId: string;
  path: string;
}

type ControlKey = 'share' | 'background_enabled';

/**
 * What an Ember in a legally sensitive category touches on, in the owner's
 * own terms (2026-09-14 晚): for such an Ember the Background switch starts
 * unanswered, and turning it on here is the owner's own yes — the consent
 * those categories need before the Ember may inform anyone else's conversation.
 */
const SENSITIVE_TOUCHES_ON: Record<NonNullable<OwnerEmberDetailResponse['ember']['background_sensitive']>, string> = {
  health: 'your health',
  sex_life_or_sexual_orientation: 'your intimate life',
  transgender_or_nonbinary_status: 'your gender',
  religion: 'your faith',
  race_or_ethnicity: 'your ethnicity or background',
  citizenship_or_immigration: 'your immigration status',
};

/**
 * S13 — Owner Ember Detail (§4.13): the one Ember page, at its home. The
 * Ember itself (EmberBody — the same drawing the chat opens in place), then
 * the owner's management of it (2026-09-12 拍板; sharing 2026-09-27): who
 * has read it and can still — each read Ember gave closable on its row, with
 * the notes between the two sides and a line back; the hand-off link
 * takeable back; `Close it to everyone` when more than one is out — and the
 * two switches this Ember carries: may it be shown to people it can help,
 * may it inform what Ember tells other people at a light. Delete in the
 * toolbar. Opening it marks the Ember's impact notifications read
 * (fire-and-forget). `#settings` lands on the management part — the chat's
 * page has one door here.
 */
export function OwnerEmberDetailPage({ emberId, path }: OwnerEmberDetailPageProps): ReactElement {
  const status = useRequireAuth(path);
  const { session } = useSession();
  const [data, setData] = useState<OwnerEmberDetailResponse | null>(null);
  const [missing, setMissing] = useState<boolean | 'deleted'>(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<ControlKey | null>(null);
  const [confirm, setConfirm] = useState<null | 'delete' | 'close_all'>(null);
  const [acting, setActing] = useState(false);
  const scrolledToSettings = useRef(false);

  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    let alive = true;
    emberApi.ownerEmberDetail(emberId)
      .then((d) => {
        if (!alive) return;
        setData(d);
        // The readers' notes shown on this page are looked at: the owner's
        // read_note letters end, the way ReadPage ends the reader's
        // (2026-10-03: two owners came here from the notice and the notes
        // stayed unseen — the page never said so).
        for (const r of d.ember.share?.reads ?? []) {
          if (r.notes.some((n) => n.by === 'reader' && !n.seen)) emberApi.markReadSeen(r.id).then(() => notifyKnocksChanged()).catch(() => { /* next time */ });
        }
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isAuthRequired(err)) navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true });
        else if ((err as { status?: number }).status === 404) {
          setMissing(true);
          trackFailed('owner_ember_load', 'not_yours', { ember_id: emberId });
        } else if ((err as { status?: number }).status === 410) {
          setMissing('deleted');
          trackFailed('owner_ember_load', 'deleted', { ember_id: emberId });
        } else {
          setFailed(true);
          trackFailed('owner_ember_load', 'load_failed', { ember_id: emberId });
        }
      });
    return () => { alive = false; };
  }, [emberId, status, path]);

  // Media (header image / music) still resolving → poll every 5s, up to 2
  // minutes, then stop. No spinner, no placeholder: the page simply gains the
  // header when it arrives. The pending bits — not the missing URLs — are the
  // signal: an Ember with no briefs has nothing coming, so polling it would
  // just be 2 minutes of no-op requests.
  const mediaUnresolved = !!data
    && (data.ember.media?.header_image_pending === true
      || data.ember.media?.music_pending === true);
  // Since 2026-10-21 the live channel says when this Ember changed (live.ts `ember`: written, its picture or music
  // landed) and the page reads again then, and on each return to the tab — no 5-second clock (it ran 24 times).
  useEffect(() => {
    if (!mediaUnresolved) return undefined;
    const again = (): void => {
      emberApi.ownerEmberDetail(emberId)
        .then((d) => setData(d))
        .catch(() => { /* keep current view */ });
    };
    const onLive = (e: Event): void => {
      const ev = (e as CustomEvent<LiveEvent>).detail;
      if (!ev?.id || ev.id === emberId) again();
    };
    const onVisible = (): void => { if (document.visibilityState === 'visible') again(); };
    window.addEventListener(LIVE_EMBER_EVENT, onLive);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(LIVE_EMBER_EVENT, onLive);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [mediaUnresolved, emberId]);

  // Arrived from the chat's page through `Go to this Ember’s settings`
  // (#settings): land on the management part once the page has it.
  useEffect(() => {
    if (!data || scrolledToSettings.current || window.location.hash !== '#settings') return;
    scrolledToSettings.current = true;
    document.getElementById('settings')?.scrollIntoView({ block: 'start' });
  }, [data]);

  if (status !== 'authenticated') return <div className="ember-page-quiet">One moment…</div>;
  if (missing === 'deleted') return <PageFailure title="This Ember was deleted" logState="deleted" />;
  if (missing) {
    return <PageFailure title="This Ember isn't yours to see" logState="not_yours" />;
  }
  if (failed) return <PageFailure title="Couldn't load this Ember" detail="Refresh to try again." logState="load_failed" />;
  if (!data) return <PageLoading />;

  const { ember, impact } = data;
  // Sharing (§4.9, 2026-09-27): the reads Ember gave of this Ember — given
  // and not opened, read, closed — and who can read it right now: each read
  // still open, and the hand-off link.
  const share = ember.share;
  const reads: OwnerRead[] = share?.reads ?? [];
  const openReads = reads.filter((r) => r.state === 'accepted' || r.state === 'opened').length;
  // The stack under the count: readers who opened it (closed ones too — the read happened), newest first.
  const readerStack = reads.filter((r) => r.opened_at).sort((a, b) => new Date(b.opened_at || 0).getTime() - new Date(a.opened_at || 0).getTime()).slice(0, READER_STACK_MAX);
  const liveAccess = openReads + (ember.handoff_path ? 1 : 0);
  const shareGlobalOff = session?.share === false;
  // Not yet answered reads as off; the first flip on is the yes.
  const shareOn = share?.share_consent === 'yes';
  const backgroundGlobalOff = session?.background_experience === false;
  // A sensitive Ember the owner has not said yes to: the switch shows off and
  // the first flip turns it on — that flip is the consent (the server stamps it).
  const backgroundUnanswered = Boolean(ember.background_sensitive) && ember.background_consented !== true;
  const backgroundOn = ember.background_enabled && !backgroundUnanswered;

  // One PATCH for the Background switch; optimistic, rolled back on failure.
  const flipBackground = (): void => {
    if (busy) return;
    const before = ember.background_enabled;
    const next = backgroundUnanswered ? true : !before;
    setBusy('background_enabled');
    setData((d) => (d ? { ...d, ember: { ...d.ember, background_enabled: next } } : d));
    emberApi.patchEmberControls(ember.id, { background_enabled: next })
      .then((r) => setData((d) => (d ? { ...d, ember: { ...d.ember, ...r } } : d)))
      .catch(() => {
        trackFailed('ember_background_toggle', 'patch_failed', { ember_id: ember.id });
        setData((d) => (d ? { ...d, ember: { ...d.ember, background_enabled: before } } : d));
        toast.error("Couldn't save that change. Try again.");
      })
      .finally(() => setBusy(null));
  };

  // The sharing switch (§4.9): the owner's yes or no on this one Ember.
  const flipShare = (): void => {
    if (busy || !share) return;
    const next: 'yes' | 'no' = shareOn ? 'no' : 'yes';
    const before = share.share_consent;
    setBusy('share');
    setData((d) => (d && d.ember.share ? { ...d, ember: { ...d.ember, share: { ...d.ember.share, share_consent: next } } } : d));
    emberApi.setShareConsent(ember.id, next)
      .then((r) => {
        setData((d) => (d && d.ember.share ? { ...d, ember: { ...d.ember, share: { ...d.ember.share, share_consent: r.share_consent, share_answered_at: new Date().toISOString() } } } : d));
        // An answer here settles the ask waiting on the paper and the door line.
        notifyKnocksChanged();
      })
      .catch(() => {
        trackFailed('share_toggle', 'patch_failed', { ember_id: ember.id });
        setData((d) => (d && d.ember.share ? { ...d, ember: { ...d.ember, share: { ...d.ember.share, share_consent: before } } } : d));
        toast.error("Couldn't save that change. Try again.");
      })
      .finally(() => setBusy(null));
  };

  const patchRead = (readId: string, patch: Partial<OwnerRead>): void => {
    setData((d) => (d && d.ember.share ? {
      ...d,
      ember: { ...d.ember, share: { ...d.ember.share, reads: d.ember.share.reads.map((r) => (r.id === readId ? { ...r, ...patch } : r)) } },
    } : d));
  };

  const closeRead = (read: OwnerRead): void => {
    if (acting) return;
    setActing(true);
    emberApi.closeRead(read.id)
      .then(() => {
        patchRead(read.id, { state: 'closed', closed_at: new Date().toISOString() });
        notifyKnocksChanged();
        toast.success('Closed. They can’t open it any more.');
      })
      .catch(() => {
        trackFailed('read_close', 'request_failed', { ember_id: ember.id });
        toast.error("Couldn't close it. Try again.");
      })
      .finally(() => setActing(false));
  };

  const closeToEveryone = (): void => {
    if (acting) return;
    setActing(true);
    emberApi.closeEmberToEveryone(ember.id)
      .then(({ closed }) => {
        const stamp = new Date().toISOString();
        setData((d) => (d ? {
          ...d,
          ember: {
            ...d.ember,
            handoff_path: null,
            share: d.ember.share ? { ...d.ember.share, reads: d.ember.share.reads.map((r) => (r.state === 'closed' ? r : { ...r, state: 'closed', closed_at: stamp })) } : d.ember.share,
          },
        } : d));
        notifyKnocksChanged();
        setConfirm(null);
        toast.success(closed > 0 ? 'Closed. It’s yours alone again.' : 'Link taken back');
      })
      .catch(() => {
        trackFailed('ember_close_all', 'request_failed', { ember_id: ember.id });
        toast.error("Couldn't close it. Try again.");
      })
      .finally(() => setActing(false));
  };

  const deleteEmber = (): void => {
    if (acting) return;
    setActing(true);
    emberApi.deleteEmber(ember.id)
      .then(() => {
        toast.success('Ember deleted');
        navigate('/me/embers', { replace: true });
      })
      .catch(() => {
        trackFailed('ember_delete_confirm', 'delete_failed', { ember_id: ember.id });
        toast.error("Couldn't delete it. Try again.");
        setActing(false);
      });
  };

  return (
    <div className="ember-container ember-reading owner-ember">
      <div className="owner-ember-toolbar">
        <PageBack to="/me/embers" className="owner-ember-back" fallbackLabel="Notebook" />
        <div className="ember-toolbar-actions">
          <PageHome />
          {/* §4.18: send this Ember to someone. */}
          <HandoffButton
            subject={{ kind: 'ember', id: ember.id }}
            title={ember.title || 'An Ember'}
            handoffPath={ember.handoff_path}
            onHandoffPath={(handoffPath) => setData((d) => (d
              ? { ...d, ember: { ...d.ember, handoff_path: handoffPath } }
              : d))}
          />
          <button
            type="button"
            className="owner-ember-delete"
            aria-label="Delete this Ember"
            data-log="ember_delete_open"
            data-log-ember-id={ember.id}
            onClick={() => setConfirm('delete')}
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 7h16" />
              <path d="M9.5 7V4.5h5V7" />
              <path d="M6.5 7l1 13h9l1-13" />
              <path d="M10 11v6M14 11v6" />
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </div>

      <EmberBody
        ember={ember}
        transcriptHref={ember.source_conversation_id ? `/me/embers/${encodeURIComponent(ember.id)}/transcript` : null}
        onTalkAbout={() => { const { about, draft } = emberAbout(ember); navigate(talkAboutHref(about, draft, path)); }}
        logSeen="owner_ember_seen"
      />

      <div id="settings" className="owner-ember-settings">
        {/* Who has read it (2026-09-05 walkthrough: one block — the reads
            Ember gave of this Ember (§4.9), each with the notes between the
            two sides, then the notes readers left; the empty line only when
            both are empty). 2026-09-12: also who CAN — each open read closes
            on its own `Close it`, the hand-off link row is here too, and
            `Close it to everyone` when more than one is out. */}
        {/* A kept-private Ember has nobody to show — unless somebody read it
            before it was kept (2026-09-24 拍板 (b)): the number is a thing
            that happened and keeping the Ember does not undo it. */}
        {(ember.status !== 'private' || ember.read_count > 0) && (
          <section className="owner-ember-impact" aria-label="Who has read it">
            <h2 className="owner-ember-section-title">Who has read it</h2>
            {/* The number itself (§4.9, 2026-09-24): the cards below say who
                is being asked and who left a note, but until now nothing said
                how many people this telling had actually reached. Counts a
                reader once, whether they are still reading or have closed it;
                never a name.

                The privacy line is part of this sentence now, not a line
                under it (2026-09-24 二次): "2 people have read it" followed by
                "Private to you. Nobody else sees this." read as a
                contradiction — it is the number that is private, not the
                Ember. With nobody yet, the list below already says "No one has
                read it yet" and nothing else needs saying. */}
            {ember.read_count > 0 && (
              <p className="owner-ember-read-count">
                <b>{ember.read_count.toLocaleString('en-US')}</b>
                {readCountLine(ember.read_count, ember.status === 'private')}
                <span className="owner-ember-read-count-note">Only you can see this number.</span>
              </p>
            )}
            {/* The readers as a stack of avatars (2026-09-30 拍板): the most
                recent who opened it, overlapping, newest first, "+N" for the
                rest of the count. Each is the reader's own avatar — an animal
                in a place from the catalogue, never a photograph, shown to no
                one else anywhere — so a returning reader is a face the owner
                comes to know, and nobody is a name. Not a link. */}
            {readerStack.length > 0 && (
              <div className="owner-ember-reader-stack" aria-label={`${ember.read_count} ${ember.read_count === 1 ? 'reader' : 'readers'}`}>
                {readerStack.map((r) => (
                  <span key={r.id} className="owner-ember-reader"><PresenceAvatar avatarKey={r.reader_avatar || assignedAvatarComboKey(r.id)} size={28} /></span>
                ))}
                {ember.read_count > readerStack.length && <span className="owner-ember-reader-more">+{(ember.read_count - readerStack.length).toLocaleString('en-US')}</span>}
              </div>
            )}
            {reads.length > 0 && (
              <div className="owner-ember-reads">
                {reads.map((r) => (
                  <ReadRow key={r.id} read={r} emberId={ember.id} acting={acting} onNote={(note) => patchRead(r.id, { notes: [...r.notes, note] })} onClose={() => closeRead(r)} />
                ))}
              </div>
            )}
            {/* "No one has read it yet" is the empty state of this whole
                block, so it cannot stand under a count of readers who have
                closed it again (2026-09-24). */}
            {(impact.length > 0 || (reads.length === 0 && ember.read_count === 0)) && <PrivateImpactList
              impact={impact}
              returnTo={path}
              logEmberId={ember.id}
              onAcknowledge={(resonanceId) => {
                setData((d) => (d ? {
                  ...d,
                  impact: d.impact.map((r) => (r.resonance_id === resonanceId
                    ? { ...r, acknowledged: true } : r)),
                } : d));
                emberApi.acknowledgeResonance(resonanceId).catch(() => {
                  trackFailed('impact_ack', 'patch_failed', { ember_id: ember.id });
                });
              }}
            />}
            {/* §4.18: the hand-off link, while one is out. One row: the fact
                and the way back. Taking it back kills /e/<token> at once;
                sending again later mints a fresh link. */}
            {ember.handoff_path && (
              <div className="owner-ember-card">
                <div className="owner-ember-link-row">
                  <span className="owner-ember-link-text">
                    <span className="owner-ember-link-label">Anyone with your link can read it</span>
                    <span className="owner-ember-link-help">You sent this Ember to someone. Take the link back and it stops working.</span>
                  </span>
                  <button
                    type="button"
                    className="ember-text-action owner-ember-link-revoke"
                    data-log="ember_handoff_revoke"
                    data-log-ember-id={ember.id}
                    onClick={() => {
                      emberApi.revokeHandoff(ember.id)
                        .then(() => {
                          setData((d) => (d ? { ...d, ember: { ...d.ember, handoff_path: null } } : d));
                          toast.success('Link taken back');
                        })
                        .catch(() => {
                          trackFailed('ember_handoff_revoke', 'delete_failed', { ember_id: ember.id });
                          toast.error("Couldn't take the link back. Try again.");
                        });
                    }}
                  >
                    Take the link back
                  </button>
                </div>
              </div>
            )}
            {liveAccess > 1 && (
              <button
                type="button"
                className="ember-text-action owner-ember-close-all"
                data-log="ember_close_all"
                data-log-ember-id={ember.id}
                onClick={() => setConfirm('close_all')}
              >
                Close it to everyone
              </button>
            )}
          </section>
        )}

        {/* The two switches this Ember carries (§4.13, 2026-09-12). Each is
            AND-ed with its account-wide twin in Settings: when that one is
            off, the row says so and rests. */}
        <section className="owner-ember-controls" aria-label="This Ember’s settings">
          <h2 className="owner-ember-section-title">This Ember’s settings</h2>
          <p className="owner-ember-section-sub">Just this one. The same switches for all your Embers are in <Link to="/settings" data-log="nav_settings" data-log-discovery="owner_ember">Settings</Link>.</p>
          <div className="owner-ember-card">
            <ul className="control-rows">
              <ControlRow
                title="Shared with people it can help"
                unavailable={shareGlobalOff || !share || !share.worth}
                helpKey="share"
                help={(
                  <>
                    <p>Ember found something in this Ember that could help someone. With this on, when a person is standing where you stood, Ember may let them read it here. They never learn who you are, and you can close it to anyone at any time.</p>
                    <p>Anyone you’ve already let in keeps reading until you close it above.</p>
                  </>
                )}
                detail={!share || !share.worth
                  ? <span className="control-row-detail">Ember didn’t find something in this one that it would show to others.</span>
                  : controlDetail(
                    shareGlobalOff,
                    shareOn,
                    'When someone is standing where you stood, Ember may let them read this. Never who you are.',
                    share.share_consent === null && share.asked ? 'Ember asked and is waiting for you. Turn this on and people it can help may read it.' : share.share_consent === null ? 'Off. Turn this on and people it can help may read it.' : 'Off. Kept to yourself.',
                  )}
                control={(
                  <Switch
                    on={shareOn && !shareGlobalOff}
                    label="Shared with people it can help"
                    busy={busy !== null}
                    unavailable={shareGlobalOff || !share || !share.worth}
                    log="share_toggle"
                    logAttrs={{ 'data-log-ember-id': ember.id }}
                    onFlip={flipShare}
                  />
                )}
              />
              <ControlRow
                title="Let this help Ember understand similar situations"
                unavailable={backgroundGlobalOff}
                helpKey="background"
                help={(
                  <>
                    <p>Ember understands a situation like this one from the people who have lived it. With this on, this Ember is one of them: when Ember talks with someone in a similar place, or answers a question at a light, it may draw on what you went through, with your name, places and anything that could point to you left out.</p>
                    <p>No one reads your words this way, and nothing is quoted. Turn it off and this Ember stays out of all of that from then on.</p>
                  </>
                )}
                detail={backgroundUnanswered && !backgroundGlobalOff
                  ? `This one touches on ${SENSITIVE_TOUCHES_ON[ember.background_sensitive!]}, so Ember keeps it to itself unless you say so here. Turn this on and Ember may draw on it, with identifying details removed, when someone is in a similar place. No one reads your words.`
                  : controlDetail(
                    backgroundGlobalOff,
                    backgroundOn,
                    'Ember may draw on this, with identifying details removed, when someone is in a similar place. No one reads your words.',
                    'Off. Ember doesn’t draw on this one for anyone else.',
                  )}
                control={(
                  <Switch
                    on={backgroundOn && !backgroundGlobalOff}
                    label="Let this help Ember understand similar situations"
                    busy={busy !== null}
                    unavailable={backgroundGlobalOff}
                    log="ember_background_toggle"
                    logAttrs={{ 'data-log-ember-id': ember.id }}
                    onFlip={flipBackground}
                  />
                )}
              />
            </ul>
          </div>
        </section>
      </div>

      {confirm === 'delete' && (
        <BottomSheet
          onClose={() => { if (!acting) setConfirm(null); }}
          title="Delete this Ember?"
          description="It leaves your notebook, and everything Ember does with it stops: anyone Ember let read it, or who has your link, loses it, and it no longer informs Ember’s other conversations. The conversation it was written from stays yours. This cannot be undone."
        >
          <button type="button" className="ember-btn ember-btn-ghost" disabled={acting} onClick={() => setConfirm(null)}>
            Keep it
          </button>
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="ember_delete_confirm"
            data-log-ember-id={ember.id}
            disabled={acting}
            onClick={deleteEmber}
          >
            {acting ? 'Deleting…' : 'Delete it'}
          </button>
        </BottomSheet>
      )}
      {confirm === 'close_all' && (
        <BottomSheet
          onClose={() => { if (!acting) setConfirm(null); }}
          title="Close it to everyone?"
          description="Everyone Ember let read it, and anyone with your link, loses it now. What they wrote you stays here. Nothing else about this Ember changes."
        >
          <button type="button" className="ember-btn ember-btn-ghost" disabled={acting} onClick={() => setConfirm(null)}>
            Not now
          </button>
          <button type="button" className="ember-btn ember-btn-primary" disabled={acting} onClick={closeToEveryone}>
            {acting ? 'Closing…' : 'Close it'}
          </button>
        </BottomSheet>
      )}
    </div>
  );
}

/**
 * One person Ember let read this Ember (§4.9, 2026-09-27): where the read
 * stands, the notes between the two sides, a line back, and `Close it`.
 * Never a name: "Someone".
 */
function ReadRow({ read, emberId, acting, onNote, onClose }: {
  read: OwnerRead;
  emberId: string;
  acting: boolean;
  onNote: (note: ReadNote) => void;
  onClose: () => void;
}): ReactElement {
  const closed = read.state === 'closed';
  const head = closed
    ? 'Closed.'
    : read.state === 'opened'
      ? `Someone read it ${timeAgo(read.opened_at)}.`
      : 'Given, not opened yet.';
  return (
    <section className={`owner-ember-card owner-ember-read${closed ? ' is-closed' : ''}`} aria-label="A person who read it">
      <div className="owner-ember-link-row">
        <span className="owner-ember-link-text">
          <span className="owner-ember-link-label">{head}</span>
          <span className="owner-ember-link-help">
            {closed ? 'They can’t open it any more. What they wrote stays here.' : 'A person standing where you stood. They never learn who you are.'}
          </span>
        </span>
        {!closed && (
          <button
            type="button"
            className="ember-text-action owner-ember-link-revoke"
            disabled={acting}
            data-log="read_close"
            data-log-ember-id={emberId}
            data-log-read-id={read.id}
            onClick={onClose}
          >
            Close it
          </button>
        )}
      </div>
      {(read.notes.length > 0 || !closed) && (
        <div className="owner-ember-read-notes">
          <NoteThread notes={read.notes} me="owner" />
          {!closed && read.state === 'opened' && (
            <NoteComposer readId={read.id} emberId={emberId} logState="owner" placeholder={read.notes.length ? 'A line back, if you like.' : 'A line for them, if you like.'} onNote={onNote} />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The words after the numeral in "N people have read it" (§4.9). A kept-
 * private Ember says it in the past: whoever read it did so before it was
 * kept, and nobody new will.
 */
function readCountLine(count: number, kept: boolean): string {
  if (kept) return count === 1 ? ' person read it before you kept it private' : ' people read it before you kept it private';
  return count === 1 ? ' person has read it' : ' people have read it';
}

/** The state line under a switch: the account-wide switch overruling it, or its own on/off. */
function controlDetail(globalOff: boolean, on: boolean, onText: string, offText: string): ReactNode {
  if (globalOff) {
    return (
      <span className="control-row-detail">
        Off for all your Embers. Turn it back on in <Link to="/settings" data-log="nav_settings" data-log-discovery="owner_ember">Settings</Link>.
      </span>
    );
  }
  return <span className="control-row-detail">{on ? onText : offText}</span>;
}
